import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bot, Send, X, Mic, Sparkles, Volume2, VolumeX, Trash2, Coffee, Target, MessageSquareText } from "lucide-react";
import { api } from "../lib/api";
import { formatCurrencyTotals } from "../lib/money";
import { useVoice } from "../context/VoiceContext";
import { toast } from "sonner";
import AIModeSelector from "./ai/AIModeSelector";
import { useAISettings } from "./ai/aiRoutes";

const AGENT_AVATAR = "https://customer-assets.emergentagent.com/job_ai-agent-builder-135/artifacts/s8r4h7c5_%CE%A3%CF%84%CE%B9%CE%B3%CE%BC%CE%B9%CF%8C%CF%84%CF%85%CF%80%CE%BF%20%CE%BF%CE%B8%CF%8C%CE%BD%CE%B7%CF%82%202026-04-20%20214621.png";

const MOOD_FILTER = {
  default: "none",
  flirt:   "saturate(1.4) hue-rotate(-12deg)",
  money:   "saturate(1.25) brightness(1.05)",
  think:   "grayscale(0.4) brightness(1.08)",
  support: "hue-rotate(8deg) brightness(1.05)",
};

const MOOD_AURA = {
  default: "linear-gradient(135deg, #14B8A6 0%, #06B6D4 100%)",
  flirt:   "linear-gradient(135deg, #F43F5E 0%, #FB923C 100%)",
  money:   "linear-gradient(135deg, #10B981 0%, #FACC15 100%)",
  think:   "linear-gradient(135deg, #8B5CF6 0%, #06B6D4 100%)",
  support: "linear-gradient(135deg, #3B82F6 0%, #14B8A6 100%)",
};

const QUICK_PROMPTS = {
  "/crm/members": ["Summarize member tiers and statuses", "How do I export the current member list?", "Which member activity should I review?"],
  "/":          ["What should I focus on today?", "Summarize my last 3 days", "Spot opportunities in my CRM"],
  "/x":         ["Draft 3 horny tweets to drive OF subs", "What time should I post tonight?", "Reply to a flirty DM"],
  "/crm":         ["Who haven't I followed up with?", "Find collab partners in Spain", "Summarize a contact card"],
  "/crm/contacts": ["Who haven't I followed up with?", "Find collab partners in Spain", "Summarize a contact card"],
  "/deals":     ["Which deals are stuck?", "Suggest a follow-up email for stale leads", "Estimate this month's pipeline value"],
  "/finance":   ["What's my MRR trend?", "Where is the revenue leaking?", "Compare OF VIP vs JFF"],
  "/crm/ai":    ["What should I focus on today?", "Which plan proposals need a decision?", "Draft a campaign message for review"],
  "/crm/money": ["Summarize earnings per platform this month", "Which expenses grew most?", "Compare OF VIP vs JFF (per currency)"],
  "/calendar":  ["Plan my next 7 days", "Suggest a Milan content shoot", "Best time to ship the Brazil PPV?"],
  "/tours":     ["Optimize my Madrid → Craiova flight order", "Write a tour announcement tweet"],
  "/inbox":     ["Triage my top 5 fan DMs", "Draft a reply to the highest-value sext request"],
  "/dm-replies":["Make this reply hornier", "Make this reply softer", "Add a PPV upsell"],
  "/captions":  ["Caption for a leg-day shoot", "Strong CTA hooks for OF launch"],
  "/sexting":   ["Take this sext one notch dirtier", "Pivot to a tip ask"],
  "/catalog":   ["What's missing from this month's content plan?"],
};

function quickFor(path) {
  if (QUICK_PROMPTS[path]) return QUICK_PROMPTS[path];
  const root = "/" + (path.split("/")[1] || "");
  return QUICK_PROMPTS[root] || QUICK_PROMPTS["/"];
}

export default function CoPilot() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);     // {role,text,mood?}
  const [input, setInput] = useState("");
  const [fanReplyOpen, setFanReplyOpen] = useState(false);
  const [fanPlatform, setFanPlatform] = useState("OnlyFans");
  const [fanMessage, setFanMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [speak, setSpeak] = useState(() => localStorage.getItem("copilot_speak") !== "0");
  const [mood, setMood] = useState("default");
  const [listening, setListening] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Explicit mode is always an opt-in choice; the default is Safe.
  const [mode, setMode] = useState(() => (localStorage.getItem("copilot_mode") === "explicit" ? "explicit" : "safe"));
  // Provider settings are admin-only, so fetch them only once the panel is open.
  const { settings: aiSettings } = useAISettings({ enabled: open });

  const changeMode = (next) => {
    setMode(next);
    localStorage.setItem("copilot_mode", next);
  };

  // Load persisted history on first open
  useEffect(() => {
    if (open && !historyLoaded) {
      api.get("/api/copilot/history", { params: { limit: 40 } }).then(r => {
        const msgs = (r.data.messages || []).map(m => ({
          role: m.role,
          text: m.content,
          mood: m.mood,
        }));
        setMessages(msgs);
        setHistoryLoaded(true);
      }).catch(() => setHistoryLoaded(true));
    }
  }, [open, historyLoaded]);

  const clearMemory = async () => {
    if (!window.confirm("Clear all co-pilot memory?")) return;
    await api.delete("/api/copilot/history");
    setMessages([]);
    toast.success("Memory cleared");
  };

  const runBrief = async (event) => {
    const refresh = Boolean(event?.shiftKey);
    setBusy(true);
    try {
      // Served from today's cache unless explicitly refreshed (shift-click = refresh).
      const r = await api.get("/api/copilot/brief", { params: refresh ? { mode, refresh: true } : { mode } });
      const note = r.data.cached ? "\n\n(Today's cached brief — shift-click Brief to regenerate.)" : "";
      setMessages((m) => [...m, { role: "assistant", text: `${r.data.brief}${note}`, mood: "think" }]);
      if (speak && speakText) { try { speakText(r.data.brief); } catch { /* noop */ } }
    } catch (e) { toast.error(e?.response?.data?.detail || "Brief failed"); }
    finally { setBusy(false); }
  };

  const runFanReply = async (event) => {
    event.preventDefault();
    const message = fanMessage.trim();
    if (!message || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/ai/templates/builtin-explicit-dm-reply/run", {
        mode: "explicit",
        variables: { platform: fanPlatform, message },
      });
      const reply = data.text || "";
      setMessages((current) => [
        ...current,
        { role: "user", text: `Draft a reply to this fan message on ${fanPlatform}: ${message}` },
        { role: "assistant", text: reply, mood: "flirt" },
      ]);
      setFanMessage("");
      setFanReplyOpen(false);
      if (speak && speakText && reply) {
        try { speakText(reply); } catch { /* noop */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fan reply template failed");
    } finally {
      setBusy(false);
    }
  };

  const runStrategy = async () => {
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: "🎯 Master strategy plan — από Catalog (unedited / presale / live / sold) + X + Finance + CRM" }]);
    try {
      const r = await api.post("/api/copilot/strategy", { prompt: "", mode }, { timeout: 90000 });
      const plan = r.data.plan;
      const stats = r.data.stats || {};
      const hdr = `📊 *Catalog snapshot*: ${stats.catalog?.unedited || 0} unedited · ${stats.catalog?.ready_to_publish || 0} ready · ${stats.catalog?.presale || 0} presale · ${stats.catalog?.live_to_subs || 0} live · ${stats.catalog?.sold || 0} on sale\n💵 30d revenue: ${stats.revenue_30d_by_currency ? formatCurrencyTotals(stats.revenue_30d_by_currency, { decimals: 0 }) : `$${stats.revenue_30d || 0}`} · 🐦 Avg likes/post: ${stats.twitter?.avg_likes || 0}\n\n`;
      setMood("think");
      setMessages((m) => [...m, { role: "assistant", text: hdr + plan, mood: "think", strategyPlan: plan }]);
      if (speak && speakText) { try { speakText(plan); } catch { /* noop */ } }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Strategy failed";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${detail}`, mood: "support" }]);
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const autoScheduleFromPlan = async (plan) => {
    if (!plan || plan.length < 100) return toast.error("No plan to schedule from");
    setBusy(true);
    try {
      const r = await api.post("/api/copilot/strategy/auto-schedule", { plan, mode }, { timeout: 60000 });
      const { created, tweets = [], timezone: tz } = r.data;
      const lines = tweets.slice(0, 10).map(t => `• ${t.scheduled_at_local} · ${t.text.slice(0, 90)}${t.text.length > 90 ? "…" : ""}`).join("\n");
      const text = `✅ Scheduled ${created} tweets (${tz})\n\n${lines}\n\nView/edit them on /x → Scheduled tab.`;
      setMessages((m) => [...m, { role: "assistant", text, mood: "money" }]);
      toast.success(`${created} tweets scheduled`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Auto-schedule failed";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${detail}`, mood: "support" }]);
      toast.error(detail);
    } finally { setBusy(false); }
  };
  const recRef = useRef(null);
  const scrollRef = useRef(null);
  const { speakText, stopSpeaking } = useVoice() || {};

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Hide on public-facing routes (landing, public forms, login)
  const PUBLIC_PATHS = ["/", "/login", "/form", "/modelapply"];
  if (PUBLIC_PATHS.includes(location.pathname)) return null;

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/api/copilot/ask", {
        prompt: t,
        page: location.pathname,
        context: "",
        mode,
      });
      const reply = r.data.reply;
      const newMood = r.data.mood || "default";
      setMood(newMood);
      setMessages((m) => [...m, { role: "assistant", text: reply, mood: newMood }]);
      if (speak && speakText) {
        try { speakText(reply); } catch { /* noop */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${detail}`, mood: "support" }]);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const toggleVoice = () => {
    const next = !speak;
    setSpeak(next);
    localStorage.setItem("copilot_speak", next ? "1" : "0");
    if (!next && stopSpeaking) stopSpeaking();
  };

  const startListen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Speech recognition not supported on this browser"); return; }
    if (recRef.current) { try { recRef.current.stop(); } catch {} }
    const rec = new SR();
    rec.lang = "el-GR";
    rec.interimResults = false;
    rec.continuous = false;
    rec.onstart = () => setListening(true);
    rec.onresult = (ev) => {
      const txt = Array.from(ev.results).map(r => r[0].transcript).join(" ").trim();
      if (txt) { setInput(txt); send(txt); }
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recRef.current = rec;
  };

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="copilot-fab"
          className="fixed bottom-28 lg:bottom-10 right-4 lg:right-6 z-[60] rounded-full pulse-coral transition hover:scale-105"
          style={{
            width: 64, height: 64,
            background: MOOD_AURA[mood],
            border: "2px solid rgba(255,255,255,0.10)",
            boxShadow: "0 12px 32px rgba(20,184,166,0.40)",
            padding: 3,
          }}
          aria-label="Open AI co-pilot"
        >
          <img src={AGENT_AVATAR} alt="Co-pilot" className="w-full h-full rounded-full object-cover"
               style={{ filter: MOOD_FILTER[mood] }} />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full" style={{ background: "var(--gio-success)", border: "2px solid var(--gio-bg)" }} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="copilot-panel"
          className="fixed bottom-24 lg:bottom-6 right-4 lg:right-6 z-[60] w-[calc(100vw-32px)] sm:w-[400px] rounded-2xl flex flex-col fade-up"
          style={{
            background: "var(--gio-surface)",
            border: "1px solid var(--gio-border)",
            boxShadow: "var(--gio-shadow-lg)",
            maxHeight: "min(640px, calc(100vh - 140px))",
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-4" style={{ borderBottom: "1px solid var(--gio-border)" }}>
            <div className="w-11 h-11 rounded-full p-0.5" style={{ background: MOOD_AURA[mood] }}>
              <img src={AGENT_AVATAR} alt="" className="w-full h-full rounded-full object-cover" style={{ filter: MOOD_FILTER[mood] }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-[15px] flex items-center gap-1.5">
                GP App <span className="badge-grad">CO-PILOT</span>
              </div>
              <div className="text-[11px]" style={{ color: "var(--gio-muted)" }}>
                {busy ? "Thinking…" : `Aware of: ${location.pathname}`}
              </div>
            </div>
            
            {/* Admin Pass Quick Links */}
            <div className="relative group">
              <button 
                title="Admin Pass - Quick Navigation" 
                className="p-1.5 rounded hover:bg-[var(--gio-surface-hi)] transition-all"
                style={{ color: "#FF10F0" }}
                onClick={() => {
                  const menu = document.getElementById('copilot-admin-pass-menu');
                  menu?.classList.toggle('hidden');
                }}
              >
                <span className="text-lg">🔑</span>
              </button>
              
              {/* Dropdown */}
              <div
                id="copilot-admin-pass-menu"
                className="hidden absolute top-full right-0 mt-2 bg-black/95 backdrop-blur-xl border border-purple-500/30 rounded-lg shadow-2xl p-2 min-w-[180px] z-50"
              >
                <div className="text-[10px] font-bold text-purple-400 mb-1 uppercase tracking-wider px-2">
                  Quick Nav
                </div>
                <a href="/members/free" className="block px-2 py-1.5 rounded text-xs hover:bg-gray-700 text-gray-300 transition-all">
                  🆓 Free Tier
                </a>
                <a href="/members-area" className="block px-2 py-1.5 rounded text-xs hover:bg-purple-700/40 text-purple-300 transition-all">
                  ⭐ Members Area
                </a>
                <a href="/catalog" className="block px-2 py-1.5 rounded text-xs hover:bg-pink-700/40 text-pink-300 transition-all">
                  📺 Catalog
                </a>
              </div>
            </div>
            
            {/* Page Audit */}
            <button 
              onClick={() => {
                // Open audit notes for current page
                window.dispatchEvent(new CustomEvent('open-page-audit'));
              }}
              title="Page Audit Notes" 
              className="p-1.5 rounded hover:bg-[var(--gio-surface-hi)] transition-all relative" 
              style={{ color: "var(--gio-warning)" }}
            >
              <span className="text-base">📋</span>
            </button>
            
            {/* Enhanced Action Buttons with New Features */}
            <button 
              onClick={runBrief} 
              title="Daily Brief - cached once per day (Athens). Shift-click to refresh/regenerate."
              data-testid="copilot-brief"
              className="relative p-1.5 rounded-lg hover:bg-gradient-to-r hover:from-amber-900/20 hover:to-yellow-900/20 transition-all group" 
              style={{ color: "var(--gio-warning)" }}
            >
              <Coffee size={16} />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full animate-pulse group-hover:scale-125 transition-transform"></span>
            </button>
            
            <button 
              onClick={runStrategy} 
              title="Master Strategy Plan - Complete content flow: Catalog → Twitter → All Platforms with 30-day revenue analysis & AI recommendations" 
              data-testid="copilot-strategy"
              className="relative p-1.5 rounded-lg hover:bg-gradient-to-r hover:from-purple-900/20 hover:to-pink-900/20 transition-all group" 
              style={{ color: "#FF10F0" }}
            >
              <Target size={16} />
              <span className="absolute -top-1 -right-1 flex">
                <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-purple-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500"></span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setFanReplyOpen((isOpen) => !isOpen)}
              title="Draft a fan DM reply (Explicit mode locked)"
              aria-label="Open fan DM reply template"
              aria-expanded={fanReplyOpen}
              className="p-1.5 rounded hover:bg-[var(--gio-surface-hi)]"
              style={{ color: "var(--gio-primary)" }}
            >
              <MessageSquareText size={16} />
            </button>
            <button onClick={clearMemory} title="Clear memory" data-testid="copilot-clear"
              className="p-1.5 rounded hover:bg-[var(--gio-surface-hi)]" style={{ color: "var(--gio-muted-2)" }}>
              <Trash2 size={15} />
            </button>
            <button onClick={toggleVoice} title={speak ? "Mute voice" : "Enable voice"} data-testid="copilot-voice"
              className="p-1.5 rounded hover:bg-[var(--gio-surface-hi)]" style={{ color: speak ? "var(--gio-primary)" : "var(--gio-muted-2)" }}>
              {speak ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button onClick={() => setOpen(false)} data-testid="copilot-close" className="p-1.5 rounded hover:bg-[var(--gio-surface-hi)]" style={{ color: "var(--gio-muted)" }}>
              <X size={18} />
            </button>
          </div>

          {/* Mode */}
          <div className="px-4 py-2 border-b border-zinc-800">
            <AIModeSelector mode={mode} onChange={changeMode} settings={aiSettings} disabled={busy} />
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="copilot-messages">
            {fanReplyOpen && (
              <form onSubmit={runFanReply} className="space-y-2 rounded-lg border border-zinc-700 p-3">
                <div className="flex items-center justify-between gap-2">
                  <label htmlFor="copilot-fan-platform" className="text-xs font-medium" style={{ color: "var(--gio-text)" }}>
                    Fan DM reply
                  </label>
                  <span className="text-[10px] text-rose-300">Explicit · locked</span>
                </div>
                <select
                  id="copilot-fan-platform"
                  value={fanPlatform}
                  onChange={(event) => setFanPlatform(event.target.value)}
                  className="gio-input w-full !py-2"
                  disabled={busy}
                >
                  {["X", "OnlyFans", "JustForFans", "JFF Inner Circle", "RFC", "Faphouse"].map((platform) => (
                    <option key={platform} value={platform}>{platform}</option>
                  ))}
                </select>
                <textarea
                  value={fanMessage}
                  onChange={(event) => setFanMessage(event.target.value)}
                  placeholder="Fan message"
                  aria-label="Fan message"
                  rows={3}
                  className="gio-input w-full resize-y !py-2"
                  disabled={busy}
                />
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={!fanMessage.trim() || busy}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition disabled:opacity-50"
                    style={{ background: "var(--gio-primary)", color: "var(--gio-primary-contrast)" }}
                  >
                    <Send size={13} /> {busy ? "Drafting…" : "Draft reply"}
                  </button>
                </div>
              </form>
            )}
            {messages.length === 0 && (
              <div>
                <div className="text-[13px] mb-3" style={{ color: "var(--gio-muted)" }}>
                  <Sparkles size={14} className="inline mr-1" style={{ color: "var(--gio-primary)" }} />
                  Quick prompts for this page:
                </div>
                <div className="space-y-2">
                  {quickFor(location.pathname).map((q, i) => (
                    <button key={i} onClick={() => send(q)} data-testid={`copilot-quick-${i}`}
                      className="w-full text-left px-3 py-2 rounded-lg text-[13px] transition"
                      style={{
                        background: "var(--gio-surface-hi)",
                        border: "1px solid var(--gio-border)",
                        color: "var(--gio-text)",
                      }}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-[14px] whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "rounded-br-sm" : "rounded-bl-sm"}`}
                  style={{
                    background: m.role === "user" ? "var(--gio-grad)" : "var(--gio-surface-hi)",
                    color: m.role === "user" ? "var(--gio-primary-contrast)" : "var(--gio-text)",
                  }}>
                  {m.text}
                  {m.strategyPlan && (
                    <button
                      onClick={() => autoScheduleFromPlan(m.strategyPlan)}
                      disabled={busy}
                      data-testid={`copilot-autoschedule-${i}`}
                      className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-bold uppercase tracking-wider transition disabled:opacity-50"
                      style={{
                        background: "var(--gio-primary)",
                        color: "var(--gio-primary-contrast)",
                      }}
                    >
                      <Send size={12} /> Auto-schedule from this plan
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-2">
                <div className="px-3 py-2 rounded-2xl text-[14px] inline-flex items-center gap-1"
                  style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted)" }}>
                  <Dot delay={0}/><Dot delay={150}/><Dot delay={300}/>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 flex items-center gap-2" style={{ borderTop: "1px solid var(--gio-border)" }}>
            <button onClick={startListen} title="Voice input" data-testid="copilot-mic"
              className="p-2 rounded-lg transition" style={{
                background: listening ? "var(--gio-secondary)" : "var(--gio-surface-hi)",
                color: listening ? "#fff" : "var(--gio-muted)",
              }}>
              <Mic size={16} className={listening ? "animate-pulse" : ""} />
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask anything…"
              data-testid="copilot-input"
              className="gio-input flex-1 !py-2"
            />
            <button onClick={() => send()} disabled={!input.trim() || busy} data-testid="copilot-send"
              className="p-2 rounded-lg transition disabled:opacity-40" style={{ background: "var(--gio-grad)", color: "var(--gio-primary-contrast)" }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Dot({ delay }) {
  return (
    <span className="inline-block w-1.5 h-1.5 rounded-full"
      style={{
        background: "var(--gio-muted)",
        animation: `gio-bounce 1.2s ${delay}ms ease-in-out infinite`,
      }} />
  );
}

/* Bounce keyframes (injected once) */
if (typeof document !== "undefined" && !document.getElementById("gio-bounce-style")) {
  const s = document.createElement("style");
  s.id = "gio-bounce-style";
  s.textContent = `@keyframes gio-bounce{0%,100%{transform:translateY(0);opacity:0.4;}50%{transform:translateY(-4px);opacity:1;}}`;
  document.head.appendChild(s);
}
